import { jwtDecode } from "jwt-decode";
import { store } from "../store";
import { logout, updateTokens } from "../store/auth-slice";
import { publicApi } from "../store/apis";
import { handleApiError } from "../errors";

// Check if token is expired
export const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    const decoded = jwtDecode(token);
    const currentTime = Math.floor(Date.now() / 1000);
    const remainingSeconds = decoded.exp - currentTime;
    const remainingHours = remainingSeconds / 3600;

    console.log(
      `📅 TOKEN EXPIRATION: ${
        decoded.exp
      } | Expires in: ${remainingHours.toFixed(2)} hours`
    );

    // Add a 30-second buffer for network latency
    return decoded.exp < currentTime - 30;
  } catch (error) {
    console.error("❌ Error decoding token:", error);
    return true;
  }
};

// Check if token is valid
export const isTokenValid = (token) => {
  const valid = token && !isTokenExpired(token);
  console.log(`✅ Token valid: ${valid}`);
  return valid;
};

// Refresh the token if needed
export const refreshTokenIfNeeded = async () => {
  const { auth } = store.getState();
  const { accessToken, refreshToken } = auth;
  console.log("🛠️ Checking token refresh...");

  if (!accessToken || !refreshToken) {
    console.warn("⚠️ No tokens found, skipping refresh.");
    return false;
  }

  if (!isTokenExpired(accessToken)) {
    console.log("🔵 Access token is still valid, no refresh needed.");
    return true;
  }

  try {
    console.log("🔄 Refreshing access token...");
    const refreshResult = await store.dispatch(
      publicApi.endpoints.refreshToken.initiate({ refreshToken })
    );
    if (refreshResult.error) {
      handleApiError(refreshResult.error, {
        showToast: true, // Show toast notification
        onAuthError: () => {
          console.warn("⚠️ Authentication error detected, logging out...");
          store.dispatch(logout());
        },
      });
    }
    if (refreshResult.data) {
      console.log("✅ Token refreshed successfully.");
      store.dispatch(
        updateTokens({
          accessToken: refreshResult.data.accessToken,
          refreshToken: refreshResult.data.refreshToken,
        })
      );
      return true;
    } else {
      console.warn("⚠️ Token refresh failed, logging out...");
      store.dispatch(logout());
      return false;
    }
  } catch (error) {
    console.error("❌ Token refresh error:", error);
    // Handle API error properly
    handleApiError(error, {
      showToast: true, // Show toast notification
      onAuthError: () => {
        console.warn("⚠️ Authentication error detected, logging out...");
        store.dispatch(logout());
      },
    });
    // store.dispatch(logout());
    return false;
  }
};

// Set up automatic token refresh
export const setupAutoRefresh = () => {
  const { auth } = store.getState();
  const { accessToken } = auth;

  if (!accessToken) {
    console.warn("⚠️ No access token found, skipping auto-refresh setup.");
    return;
  }

  if (window.tokenRefreshTimer) {
    console.log("🔄 Clearing existing auto-refresh timer.");
    clearTimeout(window.tokenRefreshTimer);
  }

  try {
    const decoded = jwtDecode(accessToken);
    const expirationTime = decoded.exp * 1000;
    const currentTime = Date.now();
    const tokenLifetime = expirationTime - decoded.iat * 1000;
    const timeToRefresh = Math.min(
      expirationTime - currentTime - 5 * 60 * 1000, // 5 min before expiration
      tokenLifetime / 2 // Half of token lifetime
    );

    console.log(
      `⏳ Auto-refresh setup | Expires in: ${
        (expirationTime - currentTime) / 1000 / 60
      } min`
    );

    if (timeToRefresh < 1000) {
      console.log("⚠️ Token is about to expire, refreshing now...");
      refreshTokenIfNeeded();
      return;
    }

    window.tokenRefreshTimer = setTimeout(() => {
      console.log("🔄 Auto-refresh triggered...");
      refreshTokenIfNeeded().then(() => {
        setupAutoRefresh();
      });
    }, timeToRefresh);
  } catch (error) {
    console.error("❌ Error setting up auto-refresh:", error);
  }
};

// Clean up refresh timer
export const cleanupAutoRefresh = () => {
  if (window.tokenRefreshTimer) {
    console.log("🗑️ Clearing auto-refresh timer.");
    clearTimeout(window.tokenRefreshTimer);
    window.tokenRefreshTimer = null;
  }
};

// Initialize auth state from storage
export const initAuthFromStorage = () => {
  console.log("🔍 Checking stored tokens...");
  const accessToken = localStorage.getItem("accessToken");
  const refreshToken = localStorage.getItem("refreshToken");

  if (accessToken && refreshToken) {
    console.log("📦 Tokens found in storage.");
    if (isTokenValid(accessToken)) {
      console.log("✅ Stored token is valid. Setting up auto-refresh.");
      setupAutoRefresh();
    } else if (refreshToken) {
      console.log("🔄 Stored access token expired. Attempting refresh...");
      refreshTokenIfNeeded().then((success) => {
        if (success) {
          setupAutoRefresh();
        }
      });
    }
  } else {
    console.warn("⚠️ No stored tokens found.");
  }
};
